import React, { useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux';
import { Box, Stack, Typography, IconButton, CircularProgress, } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { forgotPinCode, verifyPin } from "../../../redux/slices/hyperwolf/inventoryDistribution";
import { closeCustomModalDialog, setModalLoader } from '../../../redux/slices/modalSlice';
import MDButton from 'components/MDButton';
import { showToastMsg } from "components/distributionSetting/distributionManagement/addRegionKit";

const PinCodeModal = ({ closeModal }) => {
  const { user } = useSelector(state => state.auth);
  const dispatch = useDispatch()
  const { modalData, isLoading = false, } = useSelector(state => state.modal);
  const { submitBtnLabel = "Submit", handleConfirm = () => { } } = modalData || {}

  const [pinCode, setPinCode] = useState(['', '', '', '']);
  const [activeInput, setActiveInput] = useState(0);
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  const handleForgotPin = async () => {
    try {
      setIsSendingEmail(true)
      const response = await dispatch(forgotPinCode({ email: user?.email })).unwrap();
      showToastMsg(response?.message, 'success')
    } catch (error) {
      showToastMsg(error?.message, 'error')
    } finally {
      setIsSendingEmail(false)
    }
  }

  const handleSave = async () => {
    if (isLoading) return;

    const enteredPin = pinCode.join('');
    if (enteredPin.length !== 4) {
      showToastMsg('Please enter a 4-digit pin code', 'error');
      return;
    }

    try {
      dispatch(setModalLoader(true));

      const pinResponse = await dispatch(verifyPin({ email: user?.email, pin: enteredPin })).unwrap();
      const isPinVerifies = pinResponse?.user?._id;
      if (!isPinVerifies) {
        showToastMsg(pinResponse?.message, 'error');
        return;
      }

      await handleConfirm(modalData);
      dispatch(closeCustomModalDialog());

    } catch (err) {
      showToastMsg(err, 'error');
    } finally {
      dispatch(setModalLoader(false));
    };

  }

  const handleNumberClick = (number) => {
    if (activeInput < 4) {
      const newPinCode = [...pinCode];
      newPinCode[activeInput] = number;
      setPinCode(newPinCode);

      if (activeInput < 3) {
        setActiveInput(activeInput + 1);
      }
    }
  };

  const handleInputFocus = (index) => {
    setActiveInput(index);
  };

  const handleBackspace = () => {
    const newPinCode = [...pinCode];

    if (pinCode[activeInput] !== '') {
      // Clear current active input
      newPinCode[activeInput] = '';
    } else if (activeInput > 0) {
      // Move to previous input and clear it
      setActiveInput(activeInput - 1);
      newPinCode[activeInput - 1] = '';
    }

    setPinCode(newPinCode);
  };

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key >= '0' && e.key <= '9') {
        handleNumberClick(e.key);
      } else if (e.key === 'Backspace') {
        handleBackspace();
      } else if (e.key === 'ArrowLeft') {
        setActiveInput(prev => Math.max(0, prev - 1));
      } else if (e.key === 'ArrowRight') {
        setActiveInput(prev => Math.min(3, prev + 1));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [pinCode, activeInput]);

  return (
    <Box p={2} className="pin-code-modal">
      <Box mt={3} mb={1}>
        <Typography display="flex" alignItems="baseline" justifyContent="center" gap={1} variant='h4' width='100%' fontWeight="semibold" color="#2E2E3A">
          <span style={{ lineHeight: "normal" }}>Enter Pincode</span>
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="31" viewBox="0 0 20 31" fill="none">
            <path d="M17.9938 13.1633V8.53078C17.9938 4.10239 14.4094 0.5 10 0.5C5.59063 0.5 2.00625 4.10239 2.00625 8.53078V13.1633C0.884375 13.2481 0 14.1935 0 15.3461V28.3078C0 29.517 0.975 30.5 2.17812 30.5H17.8219C19.025 30.5 20 29.517 20 28.3078V15.3461C20 14.1935 19.1156 13.2481 17.9938 13.1633ZM3.88125 8.53078C3.88125 5.14196 6.62813 2.38442 10 2.38442C13.3719 2.38442 16.1188 5.14196 16.1188 8.53078V13.1539H3.88125V8.53078ZM12.9187 27.1928C12.9719 27.4284 12.7937 27.6514 12.5531 27.6514H7.44375C7.20625 27.6514 7.02812 27.4284 7.07812 27.1928L8.18437 22.196C7.1125 21.5302 6.43125 20.2959 6.55312 18.9139C6.7 17.2902 7.99375 15.9585 9.60938 15.7795C11.6938 15.5503 13.4594 17.1834 13.4594 19.2374C13.4594 20.4874 12.8 21.5835 11.8156 22.196L12.9187 27.1928Z" fill="#0163FF" />
          </svg>
        </Typography>
        <IconButton onClick={closeModal} edge='end' size='small' style={{ position: "absolute", right: "10px", top: "10px" }}>
          <CloseIcon fontSize='medium' style={{ color: "#4D4D4D" }} />
        </IconButton>
      </Box>

      <Box className="pin-number">
        {pinCode?.map((digit, index) => (
          <input type='text' onClick={() => handleInputFocus(index)} value={digit} maxLength="1" key={index} className={activeInput === index ? 'active' : ''}
          />
        ))}
      </Box>

      <Box className="number-container">
        <Box className="number-button">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 0].map((number) => (
            <MDButton
              key={number}
              onClick={() => handleNumberClick(number.toString())}
              sx={{
                minWidth: '60px',
                minHeight: '60px',
                fontSize: '24px',
                fontWeight: 'bold'
              }}
            >
              {number}
            </MDButton>
          ))}

          {/* <BackspaceIcon  style={{cursor:"pointer"}} onClick={handleBackspace}/> */}
        </Box>

        <Box display="flex" textAlign='center' justifyContent="end" width='100%' mt={3}>
          {isSendingEmail ?
            <MDButton
              style={{
                color: "#0163FF",
                fontSize: "16px",
                fontWeight: "600",
                lineHeight: "20px",
                textDecoration: "underline",
                textDecorationStyle: "solid",
                textDecorationSkipInk: "none",
                textDecorationThickness: "auto",
                textUnderlineOffset: "auto",
                textUnderlinePosition: "from-font",
                padding: "0",
                gap: "6px",
                borderRadius: "0",
                minHeight: "auto"
              }}
            >
              Sending Email... <CircularProgress size={16} />
            </MDButton>
            : <MDButton
              onClick={() => handleForgotPin()}
              style={{
                color: "#0163FF",
                fontSize: "16px",
                fontWeight: "600",
                lineHeight: "20px",
                textDecoration: "underline",
                textDecorationStyle: "solid",
                textDecorationSkipInk: "none",
                textDecorationThickness: "auto",
                textUnderlineOffset: "auto",
                textUnderlinePosition: "from-font",
                padding: "0",
                gap: "6px",
                borderRadius: "0",
                minHeight: "auto"
              }}
            >
              Forgot Pincode?
            </MDButton>}
        </Box>
      </Box>

      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="center" spacing={1.25} mt={4} mb={2}>
        <MDButton
          onClick={() => { closeModal() }}
          sx={{ minWidth: '150px', borderColor: "#2E2E3A !important", color: "#2E2E3A !important", padding: "8px" }}
          type='button'
          variant='outlined'
          color='dark'
        >
          Cancel
        </MDButton>

        <MDButton
          onClick={() => handleSave()}
          sx={{ minWidth: '150px', padding: "8px" }}
          color="primary"
          type='button'
          variant='contained'
          loading={isLoading}
          disabled={isLoading}
        >
          {submitBtnLabel}
        </MDButton>
      </Stack>
    </Box>
  )
}

export default PinCodeModal
